import {
  EtiquetaDePagina,
  CLASE_TITULO_DE_PAGINA,
  CLASE_SUBTITULO_DE_PAGINA,
} from '@/components/patterns';
import { useEffect, useState } from 'react';
import { AppLayout } from '@/components/layout/app-layout';
import { FadeIn } from '@/components/motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/features/auth/hooks/use-auth';
import { useAuthStore } from '@/features/auth/store/auth-store';
import { apiClient } from '@/lib/api/api-client';
import { ENDPOINTS } from '@/lib/api/endpoints';
import { formatDateString } from '@/lib/utils/format';
import { useToast } from '@/lib/hooks/use-toast';
import { UserRole } from '@/types';
import { Mail, Shield, Building2, Calendar, Save, Lock, Eye, EyeOff } from 'lucide-react';

const ROLE_LABELS: Record<UserRole, string> = {
  [UserRole.PLATFORM_ADMIN]: 'Super Admin',
  [UserRole.COMPANY_ADMIN]: 'Administrador',
  [UserRole.SALESPERSON]: 'Vendedor',
  [UserRole.APPROVER]: 'Aprobador',
};

const ROLE_VARIANTS: Record<UserRole, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  [UserRole.PLATFORM_ADMIN]: 'destructive',
  [UserRole.COMPANY_ADMIN]: 'default',
  [UserRole.SALESPERSON]: 'secondary',
  [UserRole.APPROVER]: 'outline',
};

interface ProfileData {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  tenantId: string;
  tenant?: {
    id: string;
    name: string;
  };
  /** Ausente mientras se muestra el usuario de la sesión: sólo `/users/profile` lo trae. */
  createdAt?: Date | string | null;
}

export function ProfilePage() {
  const { user } = useAuth();
  const { toast } = useToast();

  // Datos del perfil
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);

  // Edicion de perfil
  const [isEditing, setIsEditing] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [isSavingProfile, setIsSavingProfile] = useState(false);

  // Cambio de contraseña
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Cargar perfil
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setIsLoadingProfile(true);
        const data = await apiClient.get<ProfileData>(ENDPOINTS.USER_PROFILE);
        setProfile(data);
        setFirstName(data.firstName);
        setLastName(data.lastName);
      } catch (error: any) {
        toast({
          title: 'Error al cargar perfil',
          description:
            error.response?.data?.error ||
            error.message ||
            'No se pudo cargar la información del perfil',
          variant: 'destructive',
        });
      } finally {
        setIsLoadingProfile(false);
      }
    };

    fetchProfile();
  }, [toast]);

  const handleStartEditing = () => {
    if (profile) {
      setFirstName(profile.firstName);
      setLastName(profile.lastName);
    }
    setIsEditing(true);
  };

  const handleCancelEditing = () => {
    if (profile) {
      setFirstName(profile.firstName);
      setLastName(profile.lastName);
    }
    setIsEditing(false);
  };

  const handleSaveProfile = async () => {
    if (!firstName.trim() || !lastName.trim()) {
      toast({
        title: 'Error de validación',
        description: 'El nombre y apellido son obligatorios',
        variant: 'destructive',
      });
      return;
    }

    const nameRegex = /^[a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s'-]+$/;
    if (!nameRegex.test(firstName.trim())) {
      toast({
        title: 'Error de validación',
        description: 'El nombre solo puede contener letras, espacios, apóstrofes y guiones',
        variant: 'destructive',
      });
      return;
    }

    if (!nameRegex.test(lastName.trim())) {
      toast({
        title: 'Error de validación',
        description: 'El apellido solo puede contener letras, espacios, apóstrofes y guiones',
        variant: 'destructive',
      });
      return;
    }

    if (firstName.trim().length < 2 || lastName.trim().length < 2) {
      toast({
        title: 'Error de validación',
        description: 'El nombre y apellido deben tener al menos 2 caracteres',
        variant: 'destructive',
      });
      return;
    }

    setIsSavingProfile(true);
    try {
      const updatedData = await apiClient.put<ProfileData>(ENDPOINTS.USER_PROFILE, {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      });

      setProfile(updatedData);
      setIsEditing(false);

      // Actualizar el store de autenticacion
      useAuthStore.getState().setUser({
        ...user!,
        firstName: updatedData.firstName,
        lastName: updatedData.lastName,
      });

      toast({
        title: 'Perfil actualizado',
        description: 'Tus datos han sido actualizados correctamente',
        variant: 'success',
      });
    } catch (error: any) {
      toast({
        title: 'Error al actualizar perfil',
        description:
          error.response?.data?.error || error.message || 'No se pudo actualizar el perfil',
        variant: 'destructive',
      });
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!currentPassword || !newPassword || !confirmNewPassword) {
      toast({
        title: 'Error de validación',
        description: 'Todos los campos de contraseña son obligatorios',
        variant: 'destructive',
      });
      return;
    }

    if (newPassword.length < 8) {
      toast({
        title: 'Error de validación',
        description: 'La nueva contraseña debe tener al menos 8 caracteres',
        variant: 'destructive',
      });
      return;
    }

    if (newPassword !== confirmNewPassword) {
      toast({
        title: 'Error de validación',
        description: 'Las contraseñas no coinciden',
        variant: 'destructive',
      });
      return;
    }

    setIsSavingPassword(true);
    try {
      await apiClient.put(ENDPOINTS.USER_PROFILE, {
        currentPassword,
        newPassword,
      });

      // Limpiar campos
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
      setShowCurrentPassword(false);
      setShowNewPassword(false);
      setShowConfirmPassword(false);

      toast({
        title: 'Contraseña actualizada',
        description: 'Tu contraseña ha sido cambiada correctamente',
        variant: 'success',
      });
    } catch (error: any) {
      toast({
        title: 'Error al cambiar contraseña',
        description:
          error.response?.data?.error || error.message || 'No se pudo cambiar la contraseña',
        variant: 'destructive',
      });
    } finally {
      setIsSavingPassword(false);
    }
  };

  const getInitials = (first: string, last: string) => {
    return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
  };

  const displayProfile =
    profile ||
    (user
      ? {
          id: user.id,
          email: user.email,
          firstName: user.firstName,
          lastName: user.lastName,
          role: user.role,
          tenantId: user.tenantId,
          tenant: user.tenant,
          /*
            El usuario de la sesión NO trae `createdAt` (el login no lo
            selecciona; sólo `/auth/me`). Antes acá iba `String(user.createdAt)`,
            que convertía el hueco en la cadena "undefined" y hacía tirar al
            formateador de fechas, y con él la pantalla entera. Se pasa el hueco
            tal cual: "Miembro desde" aparece recién cuando llega el perfil.
          */
          createdAt: user.createdAt ?? null,
        }
      : null);

  return (
    <AppLayout>
      <div className="container mx-auto p-8">
        <div className="space-y-6">
          {/* Header */}
          <FadeIn>
            <div data-tour="profile-header" className="flex items-center gap-3">
              <div>
                <div className="mb-3">
                  <EtiquetaDePagina tono="neutro">Tu cuenta</EtiquetaDePagina>
                </div>
                <h1 className={CLASE_TITULO_DE_PAGINA}>Mi Perfil</h1>
                <p className={`mt-2 ${CLASE_SUBTITULO_DE_PAGINA}`}>
                  Visualiza y edita tu información personal
                </p>
              </div>
            </div>
          </FadeIn>

          {isLoadingProfile && !displayProfile ? (
            <FadeIn delay={0.1}>
              <Card>
                <CardContent className="flex items-center justify-center py-16">
                  <p className="text-muted-foreground">Cargando perfil...</p>
                </CardContent>
              </Card>
            </FadeIn>
          ) : displayProfile ? (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Columna izquierda - Info del usuario */}
              <FadeIn delay={0.1}>
                <Card data-tour="profile-role">
                  <CardContent className="pt-6">
                    <div className="flex flex-col items-center text-center space-y-4">
                      {/* Avatar con iniciales */}
                      <div className="h-24 w-24 rounded-full bg-primary flex items-center justify-center">
                        <span className="text-3xl font-bold text-primary-foreground">
                          {getInitials(displayProfile.firstName, displayProfile.lastName)}
                        </span>
                      </div>

                      {/* Nombre completo */}
                      <div>
                        <h2 className="text-2xl font-semibold">
                          {displayProfile.firstName} {displayProfile.lastName}
                        </h2>
                        <div className="flex items-center justify-center gap-2 mt-1 text-muted-foreground">
                          <Mail className="h-4 w-4" />
                          <span className="text-sm">{displayProfile.email}</span>
                        </div>
                      </div>

                      {/* Rol */}
                      <div className="flex items-center gap-2">
                        <Shield className="h-4 w-4 text-muted-foreground" />
                        <Badge variant={ROLE_VARIANTS[displayProfile.role]}>
                          {ROLE_LABELS[displayProfile.role]}
                        </Badge>
                      </div>

                      {/* Empresa */}
                      {displayProfile.tenant && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Building2 className="h-4 w-4" />
                          <span className="text-sm">{displayProfile.tenant.name}</span>
                        </div>
                      )}

                      {/* Miembro desde — se omite mientras no haya fecha, en vez
                          de mostrar un hueco con forma de dato. */}
                      {displayProfile.createdAt && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Calendar className="h-4 w-4" />
                          <span className="text-sm">
                            Miembro desde {formatDateString(displayProfile.createdAt, 'dd/MM/yyyy')}
                          </span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </FadeIn>

              {/* Columna derecha - Edicion */}
              <div className="lg:col-span-2 space-y-6">
                {/* Editar Perfil */}
                <FadeIn delay={0.2}>
                  <Card data-tour="profile-info">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle>Datos Personales</CardTitle>
                          <CardDescription>Actualiza tu nombre y apellido</CardDescription>
                        </div>
                        {!isEditing && (
                          <Button variant="outline" onClick={handleStartEditing}>
                            Editar Perfil
                          </Button>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent>
                      {isEditing ? (
                        <div className="space-y-4">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label htmlFor="profile-firstName">Nombre</Label>
                              <Input
                                id="profile-firstName"
                                placeholder="Tu nombre"
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                                disabled={isSavingProfile}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="profile-lastName">Apellido</Label>
                              <Input
                                id="profile-lastName"
                                placeholder="Tu apellido"
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)}
                                disabled={isSavingProfile}
                              />
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="profile-email">Correo electrónico</Label>
                            <Input
                              id="profile-email"
                              type="email"
                              value={displayProfile.email}
                              disabled
                              className="bg-muted"
                            />
                            <p className="text-xs text-muted-foreground">
                              El correo electrónico no se puede modificar
                            </p>
                          </div>
                          <div className="flex justify-end gap-3 pt-2">
                            <Button
                              variant="outline"
                              onClick={handleCancelEditing}
                              disabled={isSavingProfile}
                            >
                              Cancelar
                            </Button>
                            <Button onClick={handleSaveProfile} disabled={isSavingProfile}>
                              <Save className="w-4 h-4 mr-2" />
                              {isSavingProfile ? 'Guardando...' : 'Guardar Cambios'}
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                              <p className="text-sm font-medium text-muted-foreground">Nombre</p>
                              <p className="text-sm mt-1">{displayProfile.firstName}</p>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-muted-foreground">Apellido</p>
                              <p className="text-sm mt-1">{displayProfile.lastName}</p>
                            </div>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-muted-foreground">
                              Correo electrónico
                            </p>
                            <p className="text-sm mt-1">{displayProfile.email}</p>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </FadeIn>

                {/* Cambiar contraseña */}
                <FadeIn delay={0.3}>
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Lock className="h-5 w-5" />
                        Cambiar contraseña
                      </CardTitle>
                      <CardDescription>
                        Actualiza tu contraseña de acceso al sistema
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <form onSubmit={handleChangePassword} className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="currentPassword">Contraseña actual</Label>
                          <div className="relative">
                            <Input
                              id="currentPassword"
                              type={showCurrentPassword ? 'text' : 'password'}
                              placeholder="Ingresa tu contraseña actual"
                              value={currentPassword}
                              onChange={(e) => setCurrentPassword(e.target.value)}
                              disabled={isSavingPassword}
                              className="pr-10"
                            />
                            <button
                              type="button"
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                              onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                            >
                              {showCurrentPassword ? (
                                <EyeOff className="h-4 w-4" />
                              ) : (
                                <Eye className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="newPassword">Nueva contraseña</Label>
                          <div className="relative">
                            <Input
                              id="newPassword"
                              type={showNewPassword ? 'text' : 'password'}
                              placeholder="Mínimo 8 caracteres"
                              value={newPassword}
                              onChange={(e) => setNewPassword(e.target.value)}
                              disabled={isSavingPassword}
                              className="pr-10"
                            />
                            <button
                              type="button"
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                              onClick={() => setShowNewPassword(!showNewPassword)}
                            >
                              {showNewPassword ? (
                                <EyeOff className="h-4 w-4" />
                              ) : (
                                <Eye className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="confirmNewPassword">Confirmar nueva contraseña</Label>
                          <div className="relative">
                            <Input
                              id="confirmNewPassword"
                              type={showConfirmPassword ? 'text' : 'password'}
                              placeholder="Repite la nueva contraseña"
                              value={confirmNewPassword}
                              onChange={(e) => setConfirmNewPassword(e.target.value)}
                              disabled={isSavingPassword}
                              className="pr-10"
                            />
                            <button
                              type="button"
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            >
                              {showConfirmPassword ? (
                                <EyeOff className="h-4 w-4" />
                              ) : (
                                <Eye className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                        </div>

                        <div className="flex justify-end pt-2">
                          <Button type="submit" disabled={isSavingPassword}>
                            <Lock className="w-4 h-4 mr-2" />
                            {isSavingPassword ? 'Actualizando...' : 'Cambiar contraseña'}
                          </Button>
                        </div>
                      </form>
                    </CardContent>
                  </Card>
                </FadeIn>
              </div>
            </div>
          ) : (
            <FadeIn delay={0.1}>
              <Card>
                <CardContent className="flex items-center justify-center py-16">
                  <p className="text-muted-foreground">
                    No se pudo cargar la información del perfil
                  </p>
                </CardContent>
              </Card>
            </FadeIn>
          )}
        </div>
      </div>
    </AppLayout>
  );
}

export default ProfilePage;
